import { z, ZodType } from 'zod'

import { emptySelectOptionZod, selectOptionZod } from 'components/Select'
import { paramsKind } from 'features/globalSetup/pages/Prebid/Bidders/types'
import { domainBidderPriorities } from 'features/globalSetup/types/bidderPriority'
import { Domain } from 'features/inventory/types/domain'
import { primitives } from 'types/primitive'
import { validatedAdUnitBidderParam } from './validator'

type DomainBidderSlice = { domain: Pick<Domain, 'id'> }

export const CONSTANTS = {
  AD_UNIT_BIDDER_PARAM: {
    NAME: z.string(),
    VALUE: z.string().optional(),
  },
  ID: z.string(),
} as const

const domainIds = z.object({
  domainBidderId: selectOptionZod<DomainBidderSlice>(),
  domainId: CONSTANTS.ID,
})

const adUnitBidderParams = z.record(
  CONSTANTS.AD_UNIT_BIDDER_PARAM.NAME,
  CONSTANTS.AD_UNIT_BIDDER_PARAM.VALUE,
)
export type SchemaAdUnitBidderParams = z.infer<typeof adUnitBidderParams>

const adUnitBidderParamValidation = z.object({
  kind: z.enum(paramsKind),
  name: CONSTANTS.AD_UNIT_BIDDER_PARAM.NAME,
  required: z.boolean(),
  requiredOff: z.boolean(),
  type: z.enum(primitives),
})
export type SchemaAdUnitBidderParamValidation = z.infer<typeof adUnitBidderParamValidation>

export const csEnabled: ZodType<boolean> = z.boolean()
export const s2sEnabled: ZodType<boolean> = z.boolean()
export const priority = z.enum(domainBidderPriorities)

export const adUnitBidderProps = z.object({
  _paramsValidation: z.array(adUnitBidderParamValidation),
  csDisabledByRequiredParams: z.boolean(),
  csEnabled,
  csParams: adUnitBidderParams,
  s2sDisabledByRequiredParams: z.boolean(),
  s2sEnabled,
  s2sParams: adUnitBidderParams,
})
export type SchemaAdUnitBidderProps = z.infer<typeof adUnitBidderProps>

export const schema = z
  .object({
    adUnitBidders: z.record(CONSTANTS.ID, adUnitBidderProps),
    csEnabled,
    domainIds: domainIds,
    priority,
    s2sEnabled,
  })
  .superRefine((arg: Schema, ctx: z.RefinementCtx): void => {
    Object.entries(arg.adUnitBidders).forEach(([adUnitBidderId, adUnitBidder]): void => {
      Object.entries(adUnitBidder.csParams).forEach(([paramName, paramValue]): void => {
        validatedAdUnitBidderParam({
          adUnitBidder,
          adUnitBidderId,
          ctx,
          paramKind: 'cs',
          paramName: paramName,
          paramValue,
        })
      })

      Object.entries(adUnitBidder.s2sParams).forEach(([paramName, paramValue]) => {
        validatedAdUnitBidderParam({
          adUnitBidder,
          adUnitBidderId,
          ctx,
          paramKind: 's2s',
          paramName: paramName,
          paramValue,
        })
      })
    })
  })

export type Schema = z.infer<typeof schema>

export const defaults: Schema = {
  adUnitBidders: {},
  csEnabled: false,
  domainIds: {
    domainBidderId: emptySelectOptionZod<DomainBidderSlice>(),
    domainId: '',
  },
  priority: 'primary',
  s2sEnabled: false,
} as const
